/**
 * The result strip in the short tier (Option C, C1 + C2, plan §3.5): below a 560-px viewport the answer is ONE line with
 * a Details toggle (32 px), so the form keeps its 96 px. Details shows the full strip at the top of the form's scroll
 * region, where it scrolls with the form instead of squeezing it. The line is the strip's own summary
 * (`resultStripModel`), so both heights say the same thing. Its X closes a finished result, like the docked strip's.
 */
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ResultStripModel } from './resultStripModel';

type Props = {
  model: ResultStripModel | null;
  watchdogVisible: boolean;
  expanded: boolean;
  onToggle: () => void;
  onDismiss?: () => void;
};

/** One line that says what the answer is; the full text is behind Details. */
export function testSummaryText(model: ResultStripModel | null, watchdogVisible: boolean): string | null {
  if (watchdogVisible) return 'Still trying to connect…';
  return model?.summary || null;
}

export function ConnectionTestSummary({ model, watchdogVisible, expanded, onToggle, onDismiss }: Props) {
  const text = testSummaryText(model, watchdogVisible);
  if (!text) return null;
  const tone = watchdogVisible ? 'warning' : model?.tone;
  return (
    <div data-dialog-fixed className="flex h-8 shrink-0 items-center gap-2 border-t bg-muted/30 px-4" data-testid="connection-test-summary" data-tone={tone}>
      <span role="status" className={cn('min-w-0 flex-1 truncate text-sm', tone === 'success' ? 'text-primary' : tone === 'progress' ? 'text-muted-foreground' : 'text-foreground')}>
        {text}
      </span>
      {tone !== 'progress' && (
        <Button type="button" size="xs" variant="ghost" aria-expanded={expanded} onClick={onToggle} data-testid="connection-test-details-toggle">
          {expanded ? 'Hide details' : 'Details'}
        </Button>
      )}
      {onDismiss && tone !== 'progress' && !watchdogVisible && (
        <Button type="button" variant="ghost" size="icon" className="-mr-2 h-7 w-7 shrink-0 text-muted-foreground" aria-label="Close the test result" onClick={onDismiss} data-testid="connection-test-summary-dismiss">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      )}
    </div>
  );
}
